import type { DispatchValue } from '../pipeline/dispatch';
import type { TextDetection } from '../detect/pii/types';
import type { ExifField } from '../detect/exif';
import type { FaceDetection } from '../detect/faces';
import type { PdfMetaField, PdfMetaKey } from '../detect/pdfMeta';
import type { ReviewSelection, NormalizedRect } from '../ui/types';
import type { PiiType } from '../detect/pii/types';

/**
 * Stable identifier for a single detected item. Format:
 *   text:<pageIdx>:<idxInPage>      AI / regex / NER text PII
 *   face:<idx>                      face in an image
 *   exif:<key>                      EXIF field
 *   meta:<key>                      PDF metadata field
 *   manual:<pageIdx>:<seq>          (Phase 2) drag-drawn rectangle
 *   ocr:<pageIdx>:<seq>             (Phase 5) text-in-image PII
 */
export type DetectionId = string;

export type TextEntry = {
  id: DetectionId;
  kind: 'text';
  pageIndex: number;
  detection: TextDetection;
};
export type FaceEntry = {
  id: DetectionId;
  kind: 'face';
  index: number;
  face: FaceDetection;
};
export type ExifEntry = {
  id: DetectionId;
  kind: 'exif';
  field: ExifField;
};
export type PdfMetaEntry = {
  id: DetectionId;
  kind: 'pdf-meta';
  field: PdfMetaField;
};

export type ManualEntry = {
  id: DetectionId;
  kind: 'manual';
  pageIndex: number;
  rect: NormalizedRect;
  /** Monotonic per page; used for the user-facing label. */
  seq: number;
};

/**
 * User text-highlight redaction. Holds N rectangles (one per visual
 * line of the selection) plus the source text snippet for the sidebar
 * label. Treated identically to ManualEntry by the render and output
 * paths — selectedManualsOn flattens both kinds into a single rect
 * stream.
 */
export type ManualTextEntry = {
  id: DetectionId;
  kind: 'manual-text';
  pageIndex: number;
  rects: NormalizedRect[];
  text: string;
  seq: number;
};

/**
 * OCR-derived PII hit (Phase 5). Coordinates are normalized [0..1] of
 * the page (image bitmap or rasterised PDF canvas) so the same entry
 * renders correctly at preview scale and output scale, identical to
 * manual rects. Differs from ManualTextEntry in that the user didn't
 * draw or highlight it — it's an automated suggestion that the user
 * reviews like any other AI detection.
 */
export type ImageTextEntry = {
  id: DetectionId;
  kind: 'image-text';
  pageIndex: number;
  rects: NormalizedRect[];
  text: string;
  piiType: PiiType;
  confidence: number;
};

export type Entry =
  | TextEntry
  | FaceEntry
  | ExifEntry
  | PdfMetaEntry
  | ManualEntry
  | ManualTextEntry
  | ImageTextEntry;

export type StoreSnapshot = {
  selectedIds: ReadonlySet<DetectionId>;
  currentPage: number;
  /** Total page count for PDFs; 1 for everything else. */
  pageCount: number;
};

export type Listener = (s: StoreSnapshot) => void;

const collectEntries = (value: DispatchValue): Entry[] => {
  const out: Entry[] = [];
  if (value.kind === 'text' || value.kind === 'csv') {
    value.detections.forEach((d, i) => {
      out.push({ id: `text:0:${String(i)}`, kind: 'text', pageIndex: 0, detection: d });
    });
  } else if (value.kind === 'image') {
    value.exif.fields.forEach((f) => {
      out.push({ id: `exif:${f.key}`, kind: 'exif', field: f });
    });
    value.faces.forEach((f, i) => {
      out.push({ id: `face:${String(i)}`, kind: 'face', index: i, face: f });
    });
    value.imageTextHits.forEach((h, i) => {
      out.push({
        id: `ocr:0:${String(i)}`,
        kind: 'image-text',
        pageIndex: 0,
        rects: h.rects,
        text: h.text,
        piiType: h.type,
        confidence: h.confidence,
      });
    });
  } else {
    value.pages.forEach((p) => {
      p.detections.forEach((d, i) => {
        out.push({
          id: `text:${String(p.index)}:${String(i)}`,
          kind: 'text',
          pageIndex: p.index,
          detection: d,
        });
      });
      (p.imageTextHits ?? []).forEach((h, i) => {
        out.push({
          id: `ocr:${String(p.index)}:${String(i)}`,
          kind: 'image-text',
          pageIndex: p.index,
          rects: h.rects,
          text: h.text,
          piiType: h.type,
          confidence: h.confidence,
        });
      });
    });
    value.metadata.fields.forEach((f) => {
      out.push({ id: `meta:${f.key}`, kind: 'pdf-meta', field: f });
    });
  }
  return out;
};

const pageCountFor = (value: DispatchValue): number =>
  value.kind === 'pdf' ? value.pages.length : 1;

/**
 * Single source of truth for the live preview. Holds the set of detection
 * IDs currently approved for redaction plus the visible page index.
 * Subscribers run in a single requestAnimationFrame so rapid toggle bursts
 * coalesce to one render.
 */
/** State of the asynchronous NER enrichment pass. The model is large
 *  enough that it loads after the initial dispatch finishes, and
 *  detections are appended to the sidebar as they arrive — the sidebar
 *  surfaces this status as a small banner. */
export type NerStatus = 'idle' | 'loading' | 'ready' | 'skipped' | 'error';

export class RedactionStore {
  private mutableEntries: Entry[];
  /** The dispatch value that produced these entries. Mutable because
   *  the background NER pass replaces it with an enriched copy that
   *  has additional detections lifted onto OCR words / page text. */
  private mutableValue: DispatchValue;
  private selectedIds: Set<DetectionId>;
  private currentPage = 0;
  private _pageCount: number;
  private readonly listeners = new Set<Listener>();
  private rafId: number | null = null;
  private _nerStatus: NerStatus = 'idle';
  /** Monotonic seq per page, for manual entry labels. */
  private readonly manualSeq = new Map<number, number>();

  constructor(value: DispatchValue) {
    this.mutableValue = value;
    this.mutableEntries = collectEntries(value);
    // Default: every detection on.
    this.selectedIds = new Set(this.mutableEntries.map((e) => e.id));
    this._pageCount = pageCountFor(value);
  }

  get value(): DispatchValue {
    return this.mutableValue;
  }

  get nerStatus(): NerStatus {
    return this._nerStatus;
  }

  setNerStatus(status: NerStatus): void {
    if (this._nerStatus === status) return;
    this._nerStatus = status;
    this.scheduleEmit();
  }

  /**
   * Swap in a NER-enriched dispatch value. The new value's detections
   * supersede the old AI-detected ones; manual entries (drag-rect and
   * highlight-text) are preserved verbatim. User selection state is
   * preserved by ID — entries that were toggled off stay off, new
   * entries default on.
   */
  replaceFromValue(newValue: DispatchValue): void {
    const oldEntryIds = new Set(this.mutableEntries.map((e) => e.id));
    const oldManuals = this.mutableEntries.filter(
      (e) => e.kind === 'manual' || e.kind === 'manual-text',
    );
    const aiEntries = collectEntries(newValue);
    const newEntries: Entry[] = [...aiEntries, ...oldManuals];

    const newSelected = new Set<DetectionId>();
    for (const e of newEntries) {
      if (oldEntryIds.has(e.id)) {
        if (this.selectedIds.has(e.id)) newSelected.add(e.id);
      } else {
        // Brand-new entry from NER — default on.
        newSelected.add(e.id);
      }
    }

    this.mutableValue = newValue;
    this.mutableEntries = newEntries;
    this.selectedIds = newSelected;
    this._pageCount = pageCountFor(newValue);
    this.scheduleEmit();
  }

  get entries(): readonly Entry[] {
    return this.mutableEntries;
  }

  snapshot(): StoreSnapshot {
    return {
      selectedIds: new Set(this.selectedIds),
      currentPage: this.currentPage,
      pageCount: this._pageCount,
    };
  }

  isSelected(id: DetectionId): boolean {
    return this.selectedIds.has(id);
  }

  toggle(id: DetectionId): void {
    if (this.selectedIds.has(id)) this.selectedIds.delete(id);
    else this.selectedIds.add(id);
    this.scheduleEmit();
  }

  setSelected(id: DetectionId, on: boolean): void {
    const had = this.selectedIds.has(id);
    if (on && !had) this.selectedIds.add(id);
    else if (!on && had) this.selectedIds.delete(id);
    else return;
    this.scheduleEmit();
  }

  /**
   * Toggle a batch of entries to the same state in a single emit.
   * Used by the sidebar's per-section "toggle all" control to avoid
   * triggering N preview re-renders when the user (de)selects 50
   * detections at once.
   */
  setSelectedMany(ids: readonly DetectionId[], on: boolean): void {
    let changed = false;
    for (const id of ids) {
      const had = this.selectedIds.has(id);
      if (on && !had) {
        this.selectedIds.add(id);
        changed = true;
      } else if (!on && had) {
        this.selectedIds.delete(id);
        changed = true;
      }
    }
    if (changed) this.scheduleEmit();
  }

  /**
   * Reset to the initial state: every AI-detected item selected, every
   * user-drawn manual entry deleted entirely (drawn rects + text
   * highlights). Image-text OCR hits are AI-derived so they get
   * re-selected, not removed.
   */
  resetAll(): void {
    this.mutableEntries = this.mutableEntries.filter(
      (e) => e.kind !== 'manual' && e.kind !== 'manual-text',
    );
    this.selectedIds = new Set(this.mutableEntries.map((e) => e.id));
    this.manualSeq.clear();
    this.scheduleEmit();
  }

  /**
   * Add a user-drawn rectangle to the entry list. Coords are normalized
   * [0..1] of the page so the same entry renders correctly at preview
   * scale and output scale, and for both image and PDF kinds.
   * The new entry is selected by default.
   */
  addManual(pageIndex: number, rect: NormalizedRect): ManualEntry {
    if (rect.w <= 0 || rect.h <= 0) {
      throw new Error('addManual: rect must have positive width/height');
    }
    const seq = (this.manualSeq.get(pageIndex) ?? 0) + 1;
    this.manualSeq.set(pageIndex, seq);
    const id = `manual:${String(pageIndex)}:${String(seq)}`;
    const entry: ManualEntry = { id, kind: 'manual', pageIndex, rect, seq };
    this.mutableEntries = [...this.mutableEntries, entry];
    this.selectedIds.add(id);
    this.scheduleEmit();
    return entry;
  }

  /**
   * Add a text-highlight redaction. `rects` is one rect per visual
   * line of the user's selection (multi-line selections produce
   * multiple rects). `text` is the selected substring, used as the
   * sidebar label.
   */
  addManualText(
    pageIndex: number,
    rects: readonly NormalizedRect[],
    text: string,
  ): ManualTextEntry {
    const cleaned = rects.filter((r) => r.w > 0 && r.h > 0);
    if (cleaned.length === 0) {
      throw new Error('addManualText: at least one rect with positive size required');
    }
    const seq = (this.manualSeq.get(pageIndex) ?? 0) + 1;
    this.manualSeq.set(pageIndex, seq);
    const id = `text-hl:${String(pageIndex)}:${String(seq)}`;
    const entry: ManualTextEntry = {
      id,
      kind: 'manual-text',
      pageIndex,
      rects: [...cleaned],
      text,
      seq,
    };
    this.mutableEntries = [...this.mutableEntries, entry];
    this.selectedIds.add(id);
    this.scheduleEmit();
    return entry;
  }

  /** Remove a manual entry (drawn or text-highlight). No-op for other
   *  kinds. */
  removeManual(id: DetectionId): void {
    const idx = this.mutableEntries.findIndex(
      (e) => e.id === id && (e.kind === 'manual' || e.kind === 'manual-text'),
    );
    if (idx === -1) return;
    this.mutableEntries = this.mutableEntries.filter((_, i) => i !== idx);
    this.selectedIds.delete(id);
    this.scheduleEmit();
  }

  /**
   * All selected manual rectangles on a page, flattened across both
   * drawn rectangles and text-highlight entries. Output and preview
   * paths share this so the file you download matches the canvas you
   * confirmed.
   */
  selectedManualsOn(pageIndex: number): NormalizedRect[] {
    const out: NormalizedRect[] = [];
    for (const e of this.mutableEntries) {
      if (
        e.kind !== 'manual' &&
        e.kind !== 'manual-text' &&
        e.kind !== 'image-text'
      ) {
        continue;
      }
      if (e.pageIndex !== pageIndex) continue;
      if (!this.selectedIds.has(e.id)) continue;
      if (e.kind === 'manual') out.push(e.rect);
      else out.push(...e.rects);
    }
    return out;
  }

  setPage(n: number): void {
    const clamped = Math.max(0, Math.min(this._pageCount - 1, Math.floor(n)));
    if (clamped === this.currentPage) return;
    this.currentPage = clamped;
    this.scheduleEmit();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** Selected entries on a given page (text PII). PDF-only. */
  selectedTextOn(pageIndex: number): TextDetection[] {
    const out: TextDetection[] = [];
    for (const e of this.entries) {
      if (e.kind !== 'text' || e.pageIndex !== pageIndex) continue;
      if (this.selectedIds.has(e.id)) out.push(e.detection);
    }
    return out;
  }

  /**
   * Selected text-PII entries on a page, with their ids. Used by the
   * preview's hover-to-reveal layer so a hotspot can map back to the
   * specific entry the user is hovering over and toggle it off.
   */
  selectedTextEntriesOn(pageIndex: number): TextEntry[] {
    const out: TextEntry[] = [];
    for (const e of this.entries) {
      if (e.kind !== 'text' || e.pageIndex !== pageIndex) continue;
      if (this.selectedIds.has(e.id)) out.push(e);
    }
    return out;
  }

  /**
   * Selected manual / manual-text / image-text entries on a page,
   * with their ids. Same purpose as selectedTextEntriesOn but for the
   * non-textual redactions that share the canvas.
   */
  selectedManualEntriesOn(
    pageIndex: number,
  ): (ManualEntry | ManualTextEntry | ImageTextEntry)[] {
    const out: (ManualEntry | ManualTextEntry | ImageTextEntry)[] = [];
    for (const e of this.entries) {
      if (
        e.kind !== 'manual' &&
        e.kind !== 'manual-text' &&
        e.kind !== 'image-text'
      ) {
        continue;
      }
      if (e.pageIndex !== pageIndex) continue;
      if (this.selectedIds.has(e.id)) out.push(e);
    }
    return out;
  }

  /** Selected faces (image-only). */
  selectedFaces(): FaceDetection[] {
    const out: FaceDetection[] = [];
    for (const e of this.entries) {
      if (e.kind !== 'face') continue;
      if (this.selectedIds.has(e.id)) out.push(e.face);
    }
    return out;
  }

  /**
   * Build a ReviewSelection for the current snapshot. Manuals are
   * grouped by pageIndex so the redact path can apply them per page
   * (PDF) or as a single page-0 list (image).
   */
  snapshotSelection(): ReviewSelection {
    const textPii: TextDetection[] = [];
    const faces: FaceDetection[] = [];
    const exifKeys: string[] = [];
    const pdfMetaKeys: PdfMetaKey[] = [];
    const manualsByPage = new Map<number, NormalizedRect[]>();
    for (const e of this.entries) {
      if (!this.selectedIds.has(e.id)) continue;
      if (e.kind === 'text') textPii.push(e.detection);
      else if (e.kind === 'face') faces.push(e.face);
      else if (e.kind === 'exif') exifKeys.push(e.field.key);
      else if (e.kind === 'pdf-meta') pdfMetaKeys.push(e.field.key);
      else if (e.kind === 'manual') {
        const list = manualsByPage.get(e.pageIndex) ?? [];
        list.push(e.rect);
        manualsByPage.set(e.pageIndex, list);
      } else {
        // manual-text or image-text — both carry an array of rects.
        const list = manualsByPage.get(e.pageIndex) ?? [];
        list.push(...e.rects);
        manualsByPage.set(e.pageIndex, list);
      }
    }
    return { textPii, faces, exifKeys, pdfMetaKeys, manualsByPage };
  }

  private scheduleEmit(): void {
    if (this.rafId !== null) return;
    if (typeof requestAnimationFrame === 'undefined') {
      this.emit();
      return;
    }
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.emit();
    });
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const fn of this.listeners) fn(snap);
  }
}
